import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  TrendingUp,
  Visibility,
  ShoppingCart,
  Category,
  Business,
  Refresh,
  Download,
  CalendarToday,
  Assessment,
  LocalOffer
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import api from '../../services/api';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const ProductAnalytics = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [error, setError] = useState(null);

  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Filters
  const [dateRangePreset, setDateRangePreset] = useState('30_days');

  // Fetch analytics function
  const fetchAnalytics = async (showSpinner = true) => {
    try {
      if (showSpinner) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      const response = await api.get('/admin/products/analytics/', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });

      if (response.data.success) {
        setAnalyticsData(response.data.data);
        setError(null);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to load analytics data';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch on mount and date change
  useEffect(() => {
    fetchAnalytics(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    setDateRangePreset(preset);
    const end = new Date();
    const start = new Date();

    switch (preset) {
      case '7_days':
        start.setDate(end.getDate() - 7);
        break;
      case '30_days':
        start.setDate(end.getDate() - 30);
        break;
      case '90_days':
        start.setDate(end.getDate() - 90);
        break;
      case '6_months':
        start.setMonth(end.getMonth() - 6);
        break;
      case '1_year':
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        start.setDate(end.getDate() - 30);
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  // Export functions
  const exportToCSV = () => {
    if (!analyticsData) {
      setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
      return;
    }

    const csvData = [];
    
    // Overview data
    csvData.push(['Product Analytics Report']);
    csvData.push([`Date Range: ${startDate} to ${endDate}`]);
    csvData.push([]);
    csvData.push(['Overview Metrics']);
    csvData.push(['Metric', 'Value']);
    csvData.push(['Total Products', analyticsData.overview.total_products]);
    csvData.push(['Published Products', analyticsData.overview.published_products]);
    csvData.push(['Total Views', analyticsData.overview.total_views]);
    csvData.push(['Total Sales', analyticsData.overview.total_sales]);
    csvData.push([]);

    // Category distribution
    csvData.push(['Category Distribution']);
    csvData.push(['Category', 'Product Count', 'Total Views', 'Total Sales']);
    analyticsData.category_analytics.distribution.forEach(cat => {
      csvData.push([
        cat.category__name,
        cat.product_count,
        cat.total_views || 0,
        cat.total_sales || 0
      ]);
    });

    // Convert to CSV string
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product-analytics-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    setSnackbar({ open: true, message: 'CSV exported successfully', severity: 'success' });
  };

  const exportToPDF = () => {
    if (!analyticsData) {
      setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
      return;
    }

    const doc = new jsPDF();
    let yPosition = 20;

    // Title
    doc.setFontSize(20);
    doc.setTextColor(220, 20, 60);
    doc.text('Product Analytics Report', 14, yPosition);
    yPosition += 10;

    // Date Range
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Date Range: ${startDate} to ${endDate}`, 14, yPosition);
    yPosition += 10;

    // Overview Metrics
    doc.setFontSize(14);
    doc.setTextColor(220, 20, 60);
    doc.text('Overview Metrics', 14, yPosition);
    yPosition += 8;

    doc.autoTable({
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: [
        ['Total Products', analyticsData.overview.total_products.toLocaleString()],
        ['Published Products', analyticsData.overview.published_products.toLocaleString()],
        ['Draft Products', analyticsData.overview.draft_products.toLocaleString()],
        ['Total Views', analyticsData.overview.total_views.toLocaleString()],
        ['Views (24h)', analyticsData.overview.total_views_24h.toLocaleString()],
        ['Views (7d)', analyticsData.overview.total_views_7d.toLocaleString()],
        ['Total Sales', analyticsData.overview.total_sales.toLocaleString()],
        ['Conversion Rate', `${analyticsData.conversion_metrics.conversion_rate}%`],
        ['Avg. Hotness Score', analyticsData.overview.avg_hotness_score.toFixed(2)]
      ],
      headStyles: { fillColor: [220, 20, 60] },
      theme: 'striped',
      styles: { fontSize: 10 }
    });

    yPosition = doc.lastAutoTable.finalY + 15;
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    // Category Distribution
    doc.setFontSize(14);
    doc.setTextColor(220, 20, 60);
    doc.text('Category Distribution (Top 10)', 14, yPosition);
    yPosition += 8;

    doc.autoTable({
      startY: yPosition,
      head: [['Category', 'Products', 'Total Views', 'Total Sales', 'Avg. Hotness']],
      body: analyticsData.category_analytics.distribution.slice(0, 10).map(cat => [
        cat.category__name,
        cat.product_count.toLocaleString(),
        (cat.total_views || 0).toLocaleString(),
        (cat.total_sales || 0).toLocaleString(),
        (cat.avg_hotness || 0).toFixed(2)
      ]),
      headStyles: { fillColor: [220, 20, 60] },
      theme: 'striped',
      styles: { fontSize: 9 }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${i} of ${pageCount} | Generated on ${new Date().toLocaleString()} | CheckStall Analytics`,
        14,
        doc.internal.pageSize.height - 10
      );
    }

    doc.save(`product-analytics-${startDate}-to-${endDate}.pdf`);
    setSnackbar({ open: true, message: 'PDF exported successfully', severity: 'success' });
  };

  // Prepare chart data (only if analyticsData exists)
  const viewsTimelineData = analyticsData ? {
    labels: analyticsData.views_timeline.daily.map(d => new Date(d.date).toLocaleDateString()),
    datasets: [
      {
        label: 'Daily Views',
        data: analyticsData.views_timeline.daily.map(d => d.count),
        borderColor: '#DC143C',
        backgroundColor: 'rgba(220, 20, 60, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  } : null;

  const categoryDistributionData = analyticsData ? {
    labels: analyticsData.category_analytics.distribution.slice(0, 6).map(c => c.category__name),
    datasets: [
      {
        label: 'Products',
        data: analyticsData.category_analytics.distribution.slice(0, 6).map(c => c.product_count),
        backgroundColor: [
          '#DC143C',
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF'
        ]
      }
    ]
  } : null;

  const deviceBreakdownData = analyticsData ? {
    labels: analyticsData.user_engagement.device_breakdown.map(d => d.device_type || 'Unknown'),
    datasets: [
      {
        data: analyticsData.user_engagement.device_breakdown.map(d => d.count),
        backgroundColor: ['#DC143C', '#FF6384', '#36A2EB', '#FFCE56']
      }
    ]
  } : null;

  const topCategoriesViewsData = analyticsData ? {
    labels: analyticsData.category_engagement.top_categories.map(c => c.category__name),
    datasets: [
      {
        label: 'Category Views',
        data: analyticsData.category_engagement.top_categories.map(c => c.view_count),
        backgroundColor: '#36A2EB'
      }
    ]
  } : null;

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 2.5, md: 3 }, 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2 
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 'bold', 
            color: '#DC143C',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
          }}
        >
          <Assessment sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, mr: 1, verticalAlign: 'middle' }} />
          Product Analytics Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={() => fetchAnalytics(false)}
              disabled={refreshing || loading}
              sx={{ color: '#DC143C' }}
            >
              {refreshing ? <CircularProgress size={24} /> : <Refresh />}
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportToCSV}
            disabled={!analyticsData}
            sx={{ borderColor: '#DC143C', color: '#DC143C' }}
          >
            Export CSV
          </Button>
        <Button
          variant="contained"
            startIcon={<Download />}
            onClick={exportToPDF}
            disabled={!analyticsData}
          sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
        >
            Export PDF
        </Button>
      </Box>
      </Box>

      {/* Date Range Filter */}
      <Card sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
                label="Quick Preset"
                value={dateRangePreset}
                onChange={(e) => handleDatePresetChange(e.target.value)}
                size="small"
              >
                <MenuItem value="7_days">Last 7 Days</MenuItem>
                <MenuItem value="30_days">Last 30 Days</MenuItem>
                <MenuItem value="90_days">Last 90 Days</MenuItem>
                <MenuItem value="6_months">Last 6 Months</MenuItem>
                <MenuItem value="1_year">Last Year</MenuItem>
            </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
                type="date"
              fullWidth
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
                type="date"
              fullWidth
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday sx={{ color: '#DC143C' }} />
                <Typography variant="body2">
                  {analyticsData ? analyticsData.date_range.days : '...'} days selected
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress sx={{ color: '#DC143C' }} size={60} />
        </Box>
      )}

      {/* Error State */}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Data Display - Only show when we have data */}
      {!loading && analyticsData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #DC143C 0%, #B91C3C 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                        Total Products
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {analyticsData.overview.total_products.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                        {analyticsData.overview.published_products} Published
                      </Typography>
                    </Box>
                    <LocalOffer sx={{ fontSize: 50, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #36A2EB 0%, #2E8BC0 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                        Total Views
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {analyticsData.overview.total_views.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                        {analyticsData.overview.total_views_24h} in 24h
                      </Typography>
                    </Box>
                    <Visibility sx={{ fontSize: 50, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #4BC0C0 0%, #3AA6A6 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                        Total Sales
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {analyticsData.overview.total_sales.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                        {analyticsData.conversion_metrics.conversion_rate}% Conversion
                      </Typography>
                    </Box>
                    <ShoppingCart sx={{ fontSize: 50, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #FFCE56 0%, #F5B82E 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                        Categories
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {analyticsData.category_analytics.total_categories}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                        Active Categories
                      </Typography>
                    </Box>
                    <Category sx={{ fontSize: 50, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Engagement Metrics */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Unique Users
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    {analyticsData.user_engagement.unique_users.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Unique Sessions
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#36A2EB' }}>
                    {analyticsData.user_engagement.unique_sessions.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Avg. Views/Session
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4BC0C0' }}>
                    {analyticsData.user_engagement.avg_views_per_session}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Avg. Hotness Score
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#FFCE56' }}>
                    {analyticsData.overview.avg_hotness_score.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Row 1 */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Views Over Time
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {viewsTimelineData && (
                      <Line
                        data={viewsTimelineData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: true },
                            tooltip: { mode: 'index', intersect: false }
                          },
                          scales: {
                            y: { beginAtZero: true }
                          }
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    Device Breakdown
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {deviceBreakdownData && (
                      <Doughnut
                        data={deviceBreakdownData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'bottom' }
                          }
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Row 2 */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    <Category sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Products by Category (Top 6)
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {categoryDistributionData && (
                      <Pie
                        data={categoryDistributionData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'right' }
                          }
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    Category Views
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {topCategoriesViewsData && (
                      <Bar
                        data={topCategoriesViewsData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false }
                          },
                          scales: {
                            y: { beginAtZero: true }
                          }
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top Performers */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    🔥 Top Products by Views
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Views</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Sales</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analyticsData.top_performers.by_views.slice(0, 5).map((product, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {product.title}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {product.category__name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={product.view_count} size="small" color="primary" />
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={product.sales_count} size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    💰 Top Products by Sales
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Sales</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Views</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analyticsData.top_performers.by_sales.slice(0, 5).map((product, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {product.title}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {product.category__name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={product.sales_count} size="small" color="success" />
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={product.view_count} size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Category Distribution Table */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    <Category sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Category Performance (Top 10)
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#DC143C', color: 'white' }}>
                            Category
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#DC143C', color: 'white' }}>
                            Products
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#DC143C', color: 'white' }}>
                            Total Views
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#DC143C', color: 'white' }}>
                            Total Sales
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#DC143C', color: 'white' }}>
                            Avg. Hotness
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analyticsData.category_analytics.distribution.slice(0, 10).map((category, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {category.category__name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {category.product_count.toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {(category.total_views || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {(category.total_sales || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {(category.avg_hotness || 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Merchant Performance */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    <Business sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Top Merchants (Top 10)
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Merchant</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Products</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Views</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Sales</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analyticsData.merchant_analytics.top_merchants.map((merchant, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {merchant.seller__business_name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {merchant.product_count.toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {(merchant.total_views || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {(merchant.total_sales || 0).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quality Metrics */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#DC143C' }}>
                    Quality Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4BC0C0' }}>
                          {analyticsData.quality_metrics.products_with_images}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          With Images
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF6384' }}>
                          {analyticsData.quality_metrics.products_without_images}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Without Images
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#36A2EB' }}>
                          {analyticsData.quality_metrics.products_with_variants}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          With Variants
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FFCE56' }}>
                          {analyticsData.quality_metrics.avg_images_per_product.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Avg. Images/Product
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductAnalytics;
